// MATRIZ DE CENARIOS DE FORMA — as dimensoes em que um arquivo de folha varia,
// e os valores que a Atlas sabe ler ou escrever em cada uma.
//
// Ela existe porque "o adaptador suporta X" era uma esperanca: o tradutor foi
// desenhado a partir de UM ente (a ALECE), e o proximo descobriria na pratica
// qual forma ele nao cobre. Aqui cada valor e' um simbolo tipado, todo layout
// DECLARA o que assume em cada dimensao (`LayoutDefinicao.cenarios`), e dois
// testes fecham o circulo: `validarCatalogo` recusa valor que a matriz nao
// conhece, e `cobertura-matriz.test.ts` recusa valor que nenhum exemplo
// exercita. Um valor sem fixture nao e' "suportado" — e' promessa.
//
// NIVEL 2 e' o que a matriz conhece e NAO suporta, de proposito, com motivo e
// orientacao. Divide-se em dois tipos, e a diferenca e' operacional:
//
//   `envio`      — a borda reconhece no proprio arquivo (um PDF, um pacote
//                  cifrado) e recusa nomeando o cenario;
//   `declaracao` — nenhum arquivo revela por si (um arquivo que cobre mais de
//                  uma unidade pagadora e' identico a um que cobre uma). So' o
//                  FORMATO DECLARADO da unidade diz; a unidade fica pendente de
//                  inclusao e todo envio dela e' recusado antes de ler.
//
// A matriz e' constante de codigo pelo mesmo motivo do catalogo: ampliar uma
// dimensao e' trabalho de engenharia (leitor + exemplo + teste), nao
// configuracao. Ver specs/008-hub-layouts/data-model.md §1.
export const DIMENSOES = [
    "empacotamento",
    "delimitacao",
    "cabecalho",
    "tipo-registro",
    "codificacao-fim-de-linha",
    "numeros",
    "datas-competencia",
    "identificadores",
    "granularidade",
    "campos-ausentes",
    "nome-arquivo",
    "sentido",
    "volume",
];
/**
 * Os valores suportados, por dimensao. A ordem e' a da ficha e do documento.
 *
 * `as const` e' o que faz `Valor` ser uma uniao literal: um layout que declare
 * `"xml"` em `empacotamento` nao compila — e o que compila ainda passa por
 * `validarCenarios`, porque o dado tambem chega por JSON (formato declarado).
 */
export const VALORES = {
    empacotamento: ["solto", "pacote-comprimido", "soltos-juntos", "planilha-abas", "planilha-por-arquivo"],
    delimitacao: [
        "ponto-virgula",
        "barra-vertical",
        "virgula",
        "tabulacao",
        "largura-fixa",
        "celulas-planilha",
        "sem-aspas",
        "aspas-opcionais",
        "aspas-obrigatorias",
    ],
    cabecalho: ["nome-de-coluna-ordem-livre", "posicional-sem-cabecalho", "titulos-antes-do-cabecalho"],
    "tipo-registro": ["nenhum", "abertura-detalhe-fechamento", "fechamento-com-totais"],
    "codificacao-fim-de-linha": ["utf-8", "utf-8-bom", "latin1", "crlf", "lf", "linhas-vazias-no-fim"],
    numeros: [
        "decimal-virgula",
        "decimal-ponto",
        "centavos-implicitos",
        "separador-de-milhar",
        "zeros-a-esquerda",
        "com-sinal",
    ],
    "datas-competencia": [
        "ddmmaaaa",
        "aaaammdd",
        "dd-mm-aaaa",
        "aaaa-mm-dd",
        "competencia-aaaamm",
        "competencia-aamm",
        "serial-planilha",
        "intervalo-mm-aa",
    ],
    identificadores: [
        "cpf-11-digitos",
        "cpf-sem-zeros",
        "cpf-com-mascara",
        "matricula-com-digito",
        "matricula-com-sufixo",
        "matricula-com-zeros",
        "banco-3-digitos",
        "matricula-zeros-a-esquerda",
    ],
    granularidade: ["linha-por-contrato", "somado-por-matricula-verba"],
    "campos-ausentes": [
        "nenhum",
        "sem-identificador-da-divida",
        "sem-parcela-e-total",
        "sem-situacao-funcional-derivada",
        "sem-nome",
        "sem-cpf",
    ],
    "nome-arquivo": [
        "padrao-por-competencia",
        "marca-previa-definitivo",
        "reconhecido-por-nome",
        "reconhecido-por-conteudo",
    ],
    sentido: ["entrada", "saida"],
    volume: ["volume-real-conhecido"],
};
const AMBOS = ["entrada", "saida"];
function v(d, rotulos) {
    return VALORES[d].map((valor) => ({ valor, rotulo: rotulos[valor] }));
}
export const MATRIZ_CENARIOS = {
    empacotamento: {
        rotulo: "Empacotamento",
        descricao: "Como os arquivos chegam ou saem: soltos, num pacote comprimido ou em planilha.",
        valores: v("empacotamento", {
            solto: "Um arquivo solto",
            "pacote-comprimido": "Vários arquivos num pacote comprimido (zip)",
            "soltos-juntos": "Vários arquivos soltos, enviados juntos",
            "planilha-abas": "Planilha com uma aba por arquivo lógico",
            "planilha-por-arquivo": "Uma planilha por arquivo",
        }),
        naoSuportados: [
            {
                valor: "multi-unidade",
                motivo: "O arquivo cobre mais de uma unidade pagadora (recorte por coluna de órgão).",
                orientacao: "Separe o arquivo por unidade pagadora antes de enviar; cada unidade tem a sua folha na Atlas.",
                deteccao: "declaracao",
            },
            {
                valor: "pdf",
                motivo: "PDF não é um arquivo de dados: a Atlas não extrai colunas de página.",
                orientacao: "Exporte do sistema de folha em texto delimitado, largura fixa ou planilha.",
                deteccao: "envio",
            },
            {
                valor: "assinado-ou-criptografado",
                motivo: "Arquivo assinado, cifrado ou planilha com senha não pode ser lido pela Atlas.",
                orientacao: "Envie o arquivo sem senha e sem assinatura; a proteção do envio é o canal, não o arquivo.",
                deteccao: "envio",
            },
            {
                valor: "planilha-binaria-na-saida",
                motivo: "A Atlas gera planilha só no formato aberto (xlsx); o formato binário legado (xls) é aceito apenas na entrada.",
                orientacao: "Configure o sistema de folha para importar xlsx.",
                deteccao: "declaracao",
            },
        ],
        aplicaA: AMBOS,
    },
    delimitacao: {
        rotulo: "Delimitação",
        descricao: "Como uma linha se divide em campos.",
        valores: v("delimitacao", {
            "ponto-virgula": "Delimitado por ponto e vírgula",
            "barra-vertical": "Delimitado por barra vertical",
            virgula: "Delimitado por vírgula",
            tabulacao: "Delimitado por tabulação",
            "largura-fixa": "Largura fixa, sem separador",
            "celulas-planilha": "Células de planilha",
            "sem-aspas": "Sem aspas",
            "aspas-opcionais": "Aspas opcionais em campo que contém o separador",
            "aspas-obrigatorias": "Todo campo entre aspas",
        }),
        naoSuportados: [],
        aplicaA: AMBOS,
    },
    cabecalho: {
        rotulo: "Cabeçalho",
        descricao: "Como a Atlas sabe qual coluna é qual.",
        valores: v("cabecalho", {
            "nome-de-coluna-ordem-livre": "Com cabeçalho: coluna reconhecida pelo nome, em qualquer ordem",
            "posicional-sem-cabecalho": "Sem cabeçalho: coluna reconhecida pela posição",
            "titulos-antes-do-cabecalho": "Linhas de título antes do cabeçalho, puladas por regra declarada",
        }),
        naoSuportados: [],
        aplicaA: AMBOS,
    },
    "tipo-registro": {
        rotulo: "Tipo de registro",
        descricao: "Linhas de abertura, detalhe e fechamento identificadas por um código no início.",
        valores: v("tipo-registro", {
            nenhum: "Sem tipo de registro: toda linha é dado",
            "abertura-detalhe-fechamento": "Registros de abertura, detalhe e fechamento identificados por código",
            "fechamento-com-totais": "Fechamento com quantidade e soma, conferidas contra o conteúdo",
        }),
        naoSuportados: [],
        aplicaA: AMBOS,
    },
    "codificacao-fim-de-linha": {
        rotulo: "Codificação e fim de linha",
        descricao: "Como os caracteres e as quebras de linha estão gravados.",
        valores: v("codificacao-fim-de-linha", {
            "utf-8": "UTF-8",
            "utf-8-bom": "UTF-8 com marca de ordem de bytes",
            latin1: "Latin-1 / Windows-1252",
            crlf: "Fim de linha CRLF (Windows)",
            lf: "Fim de linha LF",
            "linhas-vazias-no-fim": "Linhas vazias no fim do arquivo, ignoradas",
        }),
        naoSuportados: [],
        aplicaA: AMBOS,
    },
    numeros: {
        rotulo: "Números",
        descricao: "Como valores em dinheiro e quantidades estão escritos.",
        valores: v("numeros", {
            "decimal-virgula": "Decimal com vírgula",
            "decimal-ponto": "Decimal com ponto",
            "centavos-implicitos": "Centavos inteiros, sem separador decimal",
            "separador-de-milhar": "Com separador de milhar",
            "zeros-a-esquerda": "Zeros à esquerda",
            "com-sinal": "Com sinal",
        }),
        naoSuportados: [],
        aplicaA: AMBOS,
    },
    "datas-competencia": {
        rotulo: "Datas e competência",
        descricao: "Como datas e a competência da folha estão escritas.",
        valores: v("datas-competencia", {
            ddmmaaaa: "Dia, mês e ano sem separador (DDMMAAAA)",
            aaaammdd: "Ano, mês e dia sem separador (AAAAMMDD)",
            "dd-mm-aaaa": "Dia/mês/ano com separador",
            "aaaa-mm-dd": "Ano-mês-dia com separador",
            "competencia-aaaamm": "Competência com ano de quatro dígitos (AAAAMM)",
            "competencia-aamm": "Competência com ano de dois dígitos (AAMM)",
            "intervalo-mm-aa": "Intervalo de competências MM/AA a MM/AA",
            "serial-planilha": "Data como número serial de planilha",
        }),
        naoSuportados: [],
        aplicaA: AMBOS,
    },
    identificadores: {
        rotulo: "Identificadores",
        descricao: "Como CPF, matrícula e código de banco aparecem.",
        valores: v("identificadores", {
            "cpf-11-digitos": "CPF com 11 dígitos",
            "cpf-sem-zeros": "CPF sem os zeros à esquerda, completado pela Atlas",
            "cpf-com-mascara": "CPF com máscara (pontos e traço)",
            "matricula-com-digito": "Matrícula com dígito verificador",
            "matricula-com-sufixo": "Matrícula com sufixo",
            "matricula-com-zeros": "Matrícula e folha completadas com zeros à esquerda; a Atlas guarda sem",
            "matricula-zeros-a-esquerda": "Matrícula com zeros à esquerda",
            "banco-3-digitos": "Código de banco com três dígitos",
        }),
        naoSuportados: [],
        aplicaA: AMBOS,
    },
    granularidade: {
        rotulo: "Granularidade",
        descricao: "O que uma linha de desconto representa.",
        valores: v("granularidade", {
            "linha-por-contrato": "Uma linha por contrato",
            "somado-por-matricula-verba": "Linhas somadas por matrícula e verba",
        }),
        naoSuportados: [],
        aplicaA: AMBOS,
    },
    "campos-ausentes": {
        rotulo: "Campos ausentes",
        descricao: "O que o arquivo não traz e a Atlas deriva ou deixa de ter.",
        valores: v("campos-ausentes", {
            nenhum: "Traz tudo que o padrão Atlas traz",
            "sem-identificador-da-divida": "Sem o identificador da dívida (ADF)",
            "sem-parcela-e-total": "Sem parcela atual e total",
            "sem-situacao-funcional-derivada": "Sem situação funcional: derivada por regra declarada",
            "sem-nome": "Sem o nome do servidor",
            "sem-cpf": "Sem o CPF do servidor",
        }),
        naoSuportados: [
            {
                valor: "significado-em-tabela-externa",
                motivo: "O significado de uma coluna depende de uma tabela que só o ente tem (de-para externo).",
                orientacao: "Exporte a coluna já traduzida, ou envie a tabela para entrar no layout pela engenharia.",
                deteccao: "declaracao",
            },
        ],
        aplicaA: AMBOS,
    },
    "nome-arquivo": {
        rotulo: "Nome de arquivo",
        descricao: "Como o arquivo é nomeado e reconhecido.",
        valores: v("nome-arquivo", {
            "padrao-por-competencia": "Nome padrão com a competência",
            "marca-previa-definitivo": "Nome marca prévia ou definitivo",
            "reconhecido-por-nome": "Reconhecido pelo nome do arquivo",
            "reconhecido-por-conteudo": "Reconhecido pelo conteúdo, não pelo nome",
        }),
        naoSuportados: [],
        aplicaA: AMBOS,
    },
    sentido: {
        rotulo: "Sentido",
        descricao: "Se o layout entra (retorno, carga inicial) ou sai (movimento).",
        valores: v("sentido", { entrada: "Entrada (unidade → Atlas)", saida: "Saída (Atlas → unidade)" }),
        naoSuportados: [],
        aplicaA: AMBOS,
    },
    volume: {
        rotulo: "Volume",
        descricao: "Até o maior volume real conhecido, dentro do orçamento de tempo medido.",
        valores: v("volume", {
            "volume-real-conhecido": "Até 2.016 servidores e 4.572 linhas de descontos numa competência",
        }),
        naoSuportados: [],
        aplicaA: AMBOS,
    },
};
const NIVEL2 = new Map(DIMENSOES.flatMap((d) => MATRIZ_CENARIOS[d].naoSuportados.map((n) => [n.valor, { ...n, dimensao: d }])));
export function ehDimensao(x) {
    return DIMENSOES.includes(x);
}
export function ehValorDe(d, x) {
    return VALORES[d].includes(x);
}
export function ehValorNivel2(x) {
    return NIVEL2.has(x);
}
/** A definicao de um cenario nao suportado, com a dimensao em que vive. */
export function cenarioNaoSuportado(valor) {
    const n = NIVEL2.get(valor);
    if (!n)
        throw new Error(`cenario de nivel 2 desconhecido: "${valor}"`);
    return n;
}
/** Rotulo em linguagem de folha; o proprio codigo quando nao ha rotulo. */
export function rotuloDoValor(d, valor) {
    if (valor === "n/a")
        return "não se aplica";
    const achado = MATRIZ_CENARIOS[d].valores.find((x) => x.valor === valor);
    if (achado)
        return achado.rotulo;
    const n2 = NIVEL2.get(valor);
    return n2 ? `${n2.motivo} (não suportado)` : valor;
}
/**
 * Os problemas de uma declaracao de layout. Vazio = consistente.
 *
 * Toda dimensao presente com >= 1 valor; todo valor conhecido NA DIMENSAO;
 * nivel 2 nunca (um layout nao "suporta" o que a matriz recusa); `"n/a"` so'
 * onde a dimensao nao se aplica ao sentido; `sentido` declarado igual ao real.
 */
export function validarCenarios(c, sentido) {
    const problemas = [];
    if (!c)
        return ["cenarios ausentes"];
    for (const d of DIMENSOES) {
        const valores = c[d];
        if (!valores || valores.length === 0) {
            problemas.push(`${d}: sem valor declarado`);
            continue;
        }
        const aplica = MATRIZ_CENARIOS[d].aplicaA.includes(sentido);
        for (const valor of valores) {
            if (valor === "n/a") {
                if (aplica)
                    problemas.push(`${d}: "n/a" so' vale onde a dimensao nao se aplica a ${sentido}`);
                continue;
            }
            if (ehValorNivel2(valor)) {
                problemas.push(`${d}: "${valor}" e' cenario de nivel 2 — nao pode ser declarado como suportado`);
                continue;
            }
            if (!ehValorDe(d, valor))
                problemas.push(`${d}: valor desconhecido "${valor}"`);
        }
        if (!aplica && valores.some((x) => x !== "n/a")) {
            problemas.push(`${d}: dimensao nao se aplica a ${sentido}; declare "n/a"`);
        }
    }
    const sentidos = (c.sentido ?? []);
    if (sentidos.length > 0 && !(sentidos.length === 1 && sentidos[0] === sentido)) {
        problemas.push(`sentido: declarado [${sentidos.join(", ")}], real "${sentido}"`);
    }
    for (const chave of Object.keys(c)) {
        if (!ehDimensao(chave))
            problemas.push(`dimensao desconhecida "${chave}"`);
    }
    return problemas;
}
/** Os problemas de um formato DECLARADO por unidade — parcial e com nivel 2 permitido. */
export function validarCenariosDeclarados(c) {
    if (c == null || typeof c !== "object" || Array.isArray(c))
        return ["cenarios precisa ser um objeto"];
    const problemas = [];
    for (const [chave, valores] of Object.entries(c)) {
        if (!ehDimensao(chave)) {
            problemas.push(`dimensao desconhecida "${chave}"`);
            continue;
        }
        if (!Array.isArray(valores)) {
            problemas.push(`${chave}: precisa ser uma lista`);
            continue;
        }
        for (const valor of valores) {
            if (typeof valor !== "string")
                problemas.push(`${chave}: valor nao textual`);
            else if (!ehValorDe(chave, valor) &&
                !(ehValorNivel2(valor) && cenarioNaoSuportado(valor).dimensao === chave)) {
                problemas.push(`${chave}: valor desconhecido "${valor}"`);
            }
        }
    }
    return problemas;
}
/**
 * O layout COBRE o que a unidade declarou?
 *
 * Semantica "contem": todo valor declarado, em cada dimensao declarada, esta
 * entre os valores do layout naquela dimensao. Dimensao que a unidade nao
 * declarou nao restringe. Nivel 2 declarado nunca e' coberto — e' o que gera
 * "pendente de inclusao" com motivo.
 */
export function cobre(layout, declarado) {
    for (const d of DIMENSOES) {
        const pedidos = declarado[d];
        if (!pedidos || pedidos.length === 0)
            continue;
        const tem = new Set(layout[d]);
        for (const p of pedidos)
            if (!tem.has(p))
                return false;
    }
    return true;
}
/** Os valores de nivel 2 presentes numa declaracao — o motivo da pendencia, quando ha. */
export function nivel2Declarado(declarado) {
    const out = [];
    for (const d of DIMENSOES) {
        for (const valor of (declarado[d] ?? [])) {
            if (ehValorNivel2(valor))
                out.push(valor);
        }
    }
    return out;
}
/** A matriz serializada para a tela — rotulos e nivel 2 inclusos. */
export function matrizParaTela() {
    return {
        dimensoes: DIMENSOES.map((d) => ({
            dimensao: d,
            rotulo: MATRIZ_CENARIOS[d].rotulo,
            descricao: MATRIZ_CENARIOS[d].descricao,
            valores: [...MATRIZ_CENARIOS[d].valores],
            naoSuportados: [...MATRIZ_CENARIOS[d].naoSuportados],
        })),
    };
}
//# sourceMappingURL=matriz-cenarios.js.map